import { seedPricing, type PricingData } from '@rpl/pricing';
import { useMemo } from 'react';
import { formatMoney } from '../../lib/format';
import type { UiPrint } from '../../lib/types';
import { useStore } from '../../store/store';
import { freeShipState } from './cartModel';

/**
 * Free-shipping nudge bar (small-format offer, permanent): loose C-Type
 * orders over the threshold ship free. Hidden entirely when the cart holds
 * any frame or fine-art/custom print — those never qualify, and advertising
 * an offer the order can't reach reads as a broken promise. Below the
 * threshold it shows an "add $X more" goal bar (same behavioural family as
 * the bulk nudge); at/over it flips to the green confirmation.
 */
export function FreeShippingNudge({
  pricing,
  prints,
  subtotal,
}: {
  pricing: PricingData;
  prints: readonly UiPrint[];
  subtotal: number;
}) {
  const setStep = useStore((s) => s.setStep);
  const threshold =
    pricing.freeShipping?.threshold ?? seedPricing.freeShipping?.threshold ?? 0;
  const state = useMemo(
    () => freeShipState(threshold, prints, subtotal),
    [threshold, prints, subtotal],
  );

  if (!state.eligible) return null;

  if (state.unlocked) {
    return (
      <div className="nudge nudge--active" role="status">
        <span className="nudge__icon">✓</span>
        <div className="nudge__copy">
          <strong>FREE shipping unlocked</strong> — print orders over{' '}
          {formatMoney(state.threshold)} ship free.
        </div>
      </div>
    );
  }

  return (
    <div className="nudge" role="status">
      <span className="nudge__icon">🚚</span>
      <div className="nudge__copy">
        <strong>Add {formatMoney(state.remaining)} more</strong> for FREE shipping on this
        order (prints over {formatMoney(state.threshold)}).
        <div className="nudge__track" aria-hidden="true">
          <div className="nudge__fill" style={{ width: `${state.progress * 100}%` }} />
        </div>
      </div>
      <button type="button" className="nudge__link" onClick={() => setStep(0)}>
        Add more photos
      </button>
    </div>
  );
}
